import { Copy, MoreHorizontal } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "~/components/ui/popover";
import { Tooltip, TooltipContent, TooltipTrigger } from "~/components/ui/tooltip";
import { cn } from "~/lib/utils";
import { CopyMenuItem } from "./CopyMenuItem";
import { CopyTurnLinkButton } from "./CopyTurnLinkButton";
import { formatTurnTime } from "./formatTurnTime";

interface TurnActionsMenuProps {
  // Turn creation time (ISO); rendered as the first line, e.g. "Yesterday,
  // 5:52 PM", in the viewer's local time zone.
  createdAt: string;
  // The agent's response text for this turn. A "Copy output" row appears when
  // it's non-empty; shown on every surface.
  outputText?: string;
  // Root turn anchor id for the copy-link permalink. When absent, the copy-link
  // row is omitted even if `showCopyLink` is set.
  turnId?: string;
  // Reveal the "Copy link to turn" row. On for the read-only shared/admin
  // views; off for the live regular conversation.
  showCopyLink?: boolean;
}

// "More actions" (⋯) affordance revealed on turn hover. Opens a popover whose
// first line is the message time, followed by a "Copy output" action and, on
// the shared/admin views, "Copy link to turn". Requires a `group/turn` ancestor
// to drive the hover reveal.
export const TurnActionsMenu = ({
  createdAt,
  outputText,
  turnId,
  showCopyLink = false,
}: TurnActionsMenuProps) => {
  const time = formatTurnTime(createdAt);
  const hasOutput = !!outputText?.trim();
  return (
    <Popover>
      <Tooltip>
        <TooltipTrigger asChild>
          <PopoverTrigger asChild>
            <button
              type="button"
              aria-label="More actions"
              className={cn(
                "cursor-pointer rounded p-1 text-muted-foreground transition-[color,background-color,opacity]",
                "hover:bg-muted hover:text-foreground",
                "opacity-100 md:opacity-0 md:group-hover/turn:opacity-100 md:focus-visible:opacity-100"
              )}
            >
              <MoreHorizontal className="size-4" />
            </button>
          </PopoverTrigger>
        </TooltipTrigger>
        <TooltipContent>
          <p>More actions</p>
        </TooltipContent>
      </Tooltip>
      <PopoverContent align="start" className="w-56 p-1.5">
        {time && (
          <div className="px-2 py-1.5 text-xs font-medium text-muted-foreground">{time}</div>
        )}
        {hasOutput && (
          <CopyMenuItem
            getText={() => outputText ?? ""}
            idleLabel="Copy output"
            icon={Copy}
            successMessage="Output copied"
          />
        )}
        {showCopyLink && turnId && <CopyTurnLinkButton turnId={turnId} />}
      </PopoverContent>
    </Popover>
  );
};
